import { useEffect, useState } from 'react'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { cn } from '@/lib/utils'
import { getModuleNavigationPath } from '@/lib/utils/field-navigation'
import { getModuleLinkMeta } from '@/modules/module-link'
import { useOrganizationStore } from '@/store/organization.store'
import {
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  Circle,
} from '@phosphor-icons/react'
import { m as motion } from 'framer-motion'
import { defaultUrlTransform } from 'react-markdown'
import { useNavigate } from 'react-router-dom'

import type { ScramblCard } from '@scrambl/shared'
import {
  formatAssistantStatusLabel as formatStatusLabel,
  ModuleTypeEnum,
} from '@scrambl/shared'

import { useAssistantNavigation } from './AssistantNavigation'

// ─── Types & parsers ────────────────────────────────────────────────────────
//
// The card shape and every ```scrambl-…``` parser live in `@scrambl/shared`
// so web and mobile read the same reply identically. Re-exported here because
// this module is the web import path callers already use.

export type { ScramblCard } from '@scrambl/shared'
export {
  normalizeAssistantCardsForContext,
  parseScramblCards,
  parseScramblFollowups,
  parseScramblLink,
} from '@scrambl/shared'

// ─── Helpers ────────────────────────────────────────────────────────────────

const PRIORITY_DOT: Record<string, string> = {
  URGENT: 'bg-red-500',
  HIGH: 'bg-orange-500',
  MEDIUM: 'bg-yellow-500',
  LOW: 'bg-blue-400',
}

const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  COMPLETED: {
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  DONE: {
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  IN_PROGRESS: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-600 dark:text-blue-400',
  },
  NOT_STARTED: {
    bg: 'bg-gray-500/10',
    text: 'text-gray-500 dark:text-gray-400',
  },
  TODO: { bg: 'bg-gray-500/10', text: 'text-gray-500 dark:text-gray-400' },
  BACKLOG: { bg: 'bg-gray-500/10', text: 'text-gray-500 dark:text-gray-400' },
  ON_HOLD: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-600 dark:text-amber-400',
  },
  CANCELLED: { bg: 'bg-red-500/15', text: 'text-red-500 dark:text-red-400' },
  CANCELED: { bg: 'bg-red-500/15', text: 'text-red-500 dark:text-red-400' },
}

/** Default style for any status name not in the known map */
const DEFAULT_STATUS_STYLE = { bg: 'bg-muted', text: 'text-muted-foreground' }

function getStatusStyle(statusType?: string, statusName?: string) {
  if (statusType) {
    const s = STATUS_STYLE[statusType.toUpperCase()]
    if (s) return s
  }
  // Try matching statusName against known types (e.g. "In Progress" → IN_PROGRESS)
  if (statusName) {
    const normalized = statusName.toUpperCase().replace(/[\s-]+/g, '_')
    const s = STATUS_STYLE[normalized]
    if (s) return s
  }
  // Any status gets a default neutral style
  if (statusType || statusName) return DEFAULT_STATUS_STYLE
  return null
}

/** Returns true if the string looks like a UUID */
function isUuid(s: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(s)
}

/** Clean identifier — strip UUIDs, keep task numbers like "ASP-4" */
function cleanIdentifier(id?: string): string | null {
  if (!id) return null
  if (isUuid(id)) return null
  return id
}

function formatDueDate(
  dateStr?: string,
  formatDate?: (
    date: Date | string | null | undefined,
    formatStr?: string,
  ) => string,
): string | null {
  if (!dateStr) return null
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return null
    const diffDays = Math.ceil((date.getTime() - Date.now()) / 86400000)
    if (diffDays < -1) return `${Math.abs(diffDays)}d overdue`
    if (diffDays < 0 || diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays <= 7) return `${diffDays}d`
    return formatDate ? formatDate(date, 'MMM d') : null
  } catch {
    return null
  }
}

function isOverdue(dateStr?: string): boolean {
  if (!dateStr) return false
  try {
    return new Date(dateStr).getTime() < Date.now()
  } catch {
    return false
  }
}

// ─── Type → ModuleTypeEnum ──────────────────────────────────────────────────

const TYPE_TO_ENUM: Record<string, ModuleTypeEnum> = {
  task: ModuleTypeEnum.TASK,
  project: ModuleTypeEnum.PROJECT,
  entity: ModuleTypeEnum.ENTITY,
  contact: ModuleTypeEnum.CONTACT,
  deal: ModuleTypeEnum.DEAL,
  lead: ModuleTypeEnum.CONTACT,
  event: ModuleTypeEnum.EVENT,
  contract: ModuleTypeEnum.CONTRACT,
  invoice: ModuleTypeEnum.INVOICE,
  supplier: ModuleTypeEnum.SUPPLIER,
  note: ModuleTypeEnum.NOTE,
  document: ModuleTypeEnum.DOCUMENT,
  ticket: ModuleTypeEnum.TICKET,
  thread: ModuleTypeEnum.THREAD,
  time_entry: ModuleTypeEnum.TIME_ENTRY,
  collection: ModuleTypeEnum.CUSTOM_COLLECTION,
  form: ModuleTypeEnum.FORM,
}

function resolveModuleEnum(type: string): ModuleTypeEnum | null {
  return TYPE_TO_ENUM[type.toLowerCase()] ?? null
}

export function getAssistantCardPath(
  type: string,
  id: string,
  organizationSlug: string,
): string | null {
  const normalizedType = type.toLowerCase()

  if (normalizedType === 'timesheet_approval') {
    return `/${organizationSlug}/timesheets`
  }

  if (normalizedType === 'time_entry') {
    const query = new URLSearchParams({ entryId: id })
    return `/${organizationSlug}/my-time/timesheets?${query.toString()}`
  }

  if (normalizedType === 'my_time') {
    return `/${organizationSlug}/my-time`
  }

  const moduleEnum = resolveModuleEnum(type)
  if (!moduleEnum) return null
  return getModuleNavigationPath(moduleEnum, id, organizationSlug)
}

// ─── Animation ──────────────────────────────────────────────────────────────

const cardVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.04,
      type: 'spring' as const,
      stiffness: 500,
      damping: 35,
    },
  }),
}

// ReactMarkdown can remount its rendered subtree when surrounding chat props
// change. Remember which response lists have already entered so typing in the
// composer never replays their stagger animation.
const animatedCardLists = new Set<string>()

// ─── ModuleInlineChip ───────────────────────────────────────────────────────

export function ModuleInlineChip({
  type,
  id,
  children,
}: {
  type: string
  id: string
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const forwardNavigate = useAssistantNavigation()
  const { currentOrganization } = useOrganizationStore()
  const meta = getModuleLinkMeta(type.toUpperCase())
  const Icon = meta.icon

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!currentOrganization?.slug) return
    const path = getAssistantCardPath(type, id, currentOrganization.slug)
    if (!path) return
    if (forwardNavigate) forwardNavigate(path)
    else navigate(path)
  }

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-border/50',
        'px-1.5 py-0.5 text-xs font-medium leading-none',
        'cursor-pointer bg-card transition-colors hover:bg-muted/60',
        'mx-0.5 align-middle',
      )}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <span
        className={cn(
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded',
          meta.bgClass,
        )}
      >
        <Icon className={cn('h-2 w-2', meta.textClass)} />
      </span>
      <span className="max-w-[180px] truncate">{children}</span>
    </motion.button>
  )
}

// ─── ModuleCardList ─────────────────────────────────────────────────────────

const MAX_VISIBLE = 5

export function ModuleCardList({
  cards,
  animationKey,
}: {
  cards: ScramblCard[]
  animationKey?: string
}) {
  const navigate = useNavigate()
  const forwardNavigate = useAssistantNavigation()
  const { currentOrganization } = useOrganizationStore()
  const { formatDate } = useUserTimezone()
  const [expanded, setExpanded] = useState(false)
  const listAnimationKey =
    animationKey ?? cards.map(card => `${card.type}:${card.id}`).join('|')
  const shouldAnimate = !animatedCardLists.has(listAnimationKey)

  useEffect(() => {
    animatedCardLists.add(listAnimationKey)
  }, [listAnimationKey])

  const handleNavigate = (card: ScramblCard) => {
    if (!currentOrganization?.slug) return
    const path = getAssistantCardPath(
      card.type,
      card.id,
      currentOrganization.slug,
    )
    if (!path) return
    if (forwardNavigate) forwardNavigate(path)
    else navigate(path)
  }

  if (cards.length === 0) return null

  const visibleCards = expanded ? cards : cards.slice(0, MAX_VISIBLE)
  const hiddenCount = cards.length - MAX_VISIBLE

  return (
    <div className="my-1.5 w-full max-w-full space-y-1 overflow-hidden">
      {visibleCards.map((card, index) => {
        const meta = getModuleLinkMeta(card.type.toUpperCase())
        const Icon = meta.icon
        const priorityDot = card.priority
          ? PRIORITY_DOT[card.priority.toUpperCase()]
          : null
        const statusType = card.statusType || card.statusTypeId
        const statusLabel =
          card.statusName ||
          formatStatusLabel(card.status || card.statusId) ||
          formatStatusLabel(statusType)
        const statusStyle = getStatusStyle(statusType, statusLabel ?? undefined)
        const identifier = cleanIdentifier(card.identifier)
        const dueDateText = formatDueDate(card.dueDate, formatDate)
        const overdue = isOverdue(card.dueDate)

        return (
          <motion.button
            type="button"
            key={`${card.type}-${card.id}`}
            custom={index}
            variants={cardVariants}
            initial={shouldAnimate ? 'hidden' : false}
            animate="visible"
            onClick={() => handleNavigate(card)}
            className={cn(
              'grid w-full min-w-0 max-w-full grid-cols-[1.5rem_minmax(0,1fr)_0.75rem] items-center gap-2 rounded-lg border border-border/30',
              'overflow-hidden bg-card/60 px-2 py-2 hover:bg-muted/40',
              'group cursor-pointer text-left transition-colors',
            )}
          >
            {/* Icon */}
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                meta.bgClass,
              )}
            >
              <Icon className={cn('h-3 w-3', meta.textClass)} />
            </div>

            {/* Content */}
            <div className="min-w-0 overflow-hidden">
              {/* Name row */}
              <div className="flex w-full min-w-0 max-w-full items-center gap-1.5 overflow-hidden">
                <span
                  className="block min-w-0 flex-1 basis-0 overflow-hidden truncate whitespace-nowrap text-[13px] font-medium leading-tight text-foreground"
                  style={{ width: 0 }}
                >
                  {card.name}
                </span>
                {identifier && (
                  <span className="block max-w-[6rem] shrink-0 overflow-hidden truncate whitespace-nowrap rounded bg-muted/80 px-1 py-px font-mono text-[9px] leading-tight text-muted-foreground">
                    {identifier}
                  </span>
                )}
              </div>

              {/* Meta row — compact single line */}
              <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {statusStyle && statusLabel && (
                  <span
                    className={cn(
                      'block max-w-[7rem] overflow-hidden truncate whitespace-nowrap rounded-full px-1.5 py-px text-[9px] font-medium leading-tight',
                      statusStyle.bg,
                      statusStyle.text,
                    )}
                  >
                    {statusLabel}
                  </span>
                )}
                {priorityDot && (
                  <Circle
                    weight="fill"
                    className={cn('h-1.5 w-1.5 shrink-0', priorityDot)}
                  />
                )}
                {dueDateText && (
                  <span
                    className={cn(
                      'shrink-0 text-[9px] leading-tight',
                      overdue
                        ? 'font-medium text-red-500'
                        : 'text-muted-foreground',
                    )}
                  >
                    {dueDateText}
                  </span>
                )}
                {card.parentName && (
                  <span className="flex min-w-0 max-w-full flex-1 basis-0 items-center gap-0.5 overflow-hidden text-[9px] text-muted-foreground">
                    <CaretRight className="h-2 w-2 shrink-0" />
                    <span className="block min-w-0 max-w-full truncate whitespace-nowrap">
                      {card.parentName}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Arrow */}
            <ArrowSquareOut className="h-3 w-3 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground/40" />
          </motion.button>
        )
      })}

      {/* Show more / less */}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className="mx-auto flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted/30"
        >
          <CaretDown
            className={cn(
              'h-2.5 w-2.5 transition-transform',
              expanded && 'rotate-180',
            )}
          />
          {expanded ? 'Show less' : `${hiddenCount} more`}
        </button>
      )}
    </div>
  )
}

/**
 * react-markdown's default sanitizer only lets http(s)/mailto-style protocols
 * through — a `scrambl://record` or `scrambl-help://id` href is stripped to ""
 * BEFORE the `a` component sees it, so the chip renderers never fire and the
 * link degrades to a blank anchor (which "opens" the current page). Pass this
 * as `urlTransform` on every assistant ReactMarkdown so our own schemes
 * survive; everything else keeps the default sanitization.
 */
export function assistantUrlTransform(url: string): string {
  return /^scrambl(-\w+)?:\/\//i.test(url) ? url : defaultUrlTransform(url)
}
